import { defineStore } from "pinia";

import { QuoteRequest } from "../entities/quote";
import { AnnuitiesPremium } from "../entities/premium";
import { MaritalStatus, Title } from "./yourDetails";
import { ensureDefined } from "../utils/types";
import { PensionType } from "../entities/pensionFunds";
import { PaySurvivingDependantType } from "./pensionOptions";
import { useDependantAboutYouStore } from "./dependantAboutYou";

export enum ApplyPageStages {
    PensionWise = 0,
    Information = 1,
    DefenseLine = 2,
    AboutYou = 3,
    DependantAboutYou = 4,
    PensionFundDetails = 5,
    CompleteAndSend = 6,
    NearlyThere = 7,
}

export enum ApplyCompletedStages {
    SendDirectToLnG = "sent-to-lng",
    SendDirectToPostPack = "send-to-post-pack",
    SendDirectToDownloadAndPrint = "send-to-download",
}

export enum ApplicationStatus {
    StartingSubmission = "starting_submission",
    GeneratingPack = "generating_pack",
    PackReady = "pack_ready",
    SuccessfulSubmission = "successful_submission",
    ErrorGeneratingPack = "error_generating_pack",
    ErrorSubmission = "error_submission",
}

export enum ApplicationAction {
    PaperPackRequested = "pack_requested_to_be_printed",
    PackPrintedAndSent = "pack_printed_and_sent",
    PackDownloadedByCustomer = "pack_downloaded_by_customer",
    PackReceivedFromCustomer = "pack_received_from_customer",
    PensionFundRequested = "pension_fund_requested",
    PensionFundReceived = "pension_fund_received",
    ComissionReceived = "commission_received",
    ErrorRecieving = "error_recieving"
}

export enum ApplicationRequestErrors {
    Timeout = "timeout",
    Error = "error"
}

export enum RequestStatus {
    Idle = "idle",
    Pending = "pending",
    Succeeded = "succeeded",
    Failed = "failed"
}

export type DefenceQuestion = {
    name: string,
    label: string,
    errorLabel: string,
    extraInfoText?: string | undefined,
    failState?: boolean | undefined,
    failText?: string | undefined,
    tooltip?: string | undefined,
    value: boolean | null,
}

export type Address = {
    line1: string,
    line2: string,
    city: string,
    county: string,
    postcode: string,
    country: string,
}

export type DoctorDetails = Address & {
    name: string,
    phone: string,
}

export type AboutYou = {
    title: Title | null,
    forename: string,
    surname: string,
    dob: string,
    maritalStatus: MaritalStatus | null,
    nationalInsuranceNo: string,
    address: Address,
    phone: string,
    email: string,
    applicantAllowanceProtection: boolean | null,
    applicantLifetimeAllowanceTaxProtection: boolean | null,
    applicantRemainingIlsa: number | null,
    doctorsDetails: DoctorDetails,
    seeTheMedicalReport: boolean | null,
}

export type PensionFund = {
    type: PensionType | null,
    provider: string | null,
    address: Address,
    pensionSchemeName: string,
    planNumber: string,
    totalFund: number,
    fullValue: boolean | null,
    fullValueReason: string,
    maxTaxFreeCash: boolean | null,
    taxFreeCashAmount: number,
    pensionSharingOrder: boolean | null,
    bankruptcyOrder: boolean | null,
    earmarkingAttachmentOrder: boolean | null,
    otherCourtOrders: boolean | null,
}

export type PaymentInformation = {
    accountNumber: number | null,
    sortCode: string,
    bankName: string,
    bankBranchName: string,
    address: Address
    accountHolder: string,
    buildingSociety: string,
}

type ApplicationStore = {
    companyName: string,
    companySlug: string,
    privacyUrl: string,
    illustrationsUrl: string,
    termsConditionsUrl: string,
    keyFeaturesUrl: string;
    totalAnnualPension: number,
    quoteNumber: string,
    applicationId: number | null,
    premiumId: number,
    applicationStage: ApplyPageStages,
    defenceQuestions: DefenceQuestion[],
    applicantDetails: AboutYou[]
    pensionFunds: PensionFund[],
    pensionFundsModal: boolean,
    pensionFundsIndex: number | null,
    paymentInformation: PaymentInformation,
    applicationRequestStatus: RequestStatus,
    applicationStatus: ApplicationStatus | null,
    isManualBankEntry: boolean,
    isPolling: boolean,
}

const defenceQuestions : DefenceQuestion[] = [
    {
        name: "retirementOptions",
        label: "Have you fully researched your retirement options and compared other providers products and services?",
        errorLabel: "researched",
        failState: false,
        failText: `You may get a better deal or find more suitable features by comparing products.
            If you don't shop around you won't know what's available.`,
        value: null,
    },
    {
        name: "pensionGuidance",
        label: "Are you planning to get, or have you received, pension guidance from Pension Wise?",
        errorLabel: "pension guidance",
        failState: false,
        failText: `As the decisions you make are important and you may not be able to change your mind later on,
            you can use Pension Wise for free impartial guidance. You can visit their website
            <a href='https://www.pensionwise.gov.uk' target='_blank'>www.pensionwise.gov.uk</a> ,
            or book a phone or face-to-face appointment by calling 0800 138 3944.`,
        value: null,
    },
    {
        name: "financialAdviser",
        label: "Are you planning to get, or have you received, advice from a financial adviser?",
        errorLabel: "financial adviser",
        failState: false,
        failText: `If you're unsure or want help with the decision you're making you should contact a financial
            adviser. You can find one at
            <a
                // eslint-disable-next-line max-len, max-len
                href='
                https://www.moneyhelper.org.uk/en/pensions-and-retirement/taking-your-pension/find-a-retirement-adviser'
                target='_blank'
            >
                www.moneyhelper.org.uk/en/pensions-and-retirement/taking-your-pension/find-a-retirement-adviser
            </a>.`,
        value: null,
    },
    {
        name: "valuableBenefits",
        label: "Does your existing pension plan have any guarantees or other valuable benefits?",
        errorLabel: "existing pension",
        failState: true,
        failText: `You may lose guarantees or other valuable benefits associated with your current pension provider
            if you decide to buy an annuity. So, please do not proceed further as we do not accept applications
            with guaranteed benefits.`,
        tooltip: "If you're not sure, please answer yes and be sure to check before you make your final decision.",
        value: null,
    },
    {
        name: "costsPenalties",
        label: "Are there any costs or penalties for taking money from your existing pension pot?",
        errorLabel: "costs or penalties",
        failState: true,
        failText: "You should consider if there are other options which mean you don't need to pay costs or penalties.",
        tooltip: "If you're not sure, please answer yes and be sure to check before you make your final decision.",
        value: null,
    },
    {
        name: "tax",
        label: "For the option you're considering, do you understand how much tax you'll pay?",
        errorLabel: "tax you'll pay",
        failState: false,
        failText: `Once you have taken your tax-free lump sum, any money you take will be taxed as income.<br><br>
            This means you may not get all of the money you take. This will be added to any other
            taxable income you receive which may push you into a higher tax bracket.<br><br>
            Any money you take from your pension pot may form part of your estate for inheritance
            tax purposes when you die.`,
        value: null,
    },
    {
        name: "lumpSums",
        label: "Will you need the flexibility to withdraw lump sums of money from your pension fund later on?",
        errorLabel: "flexibility to withdraw",
        failState: true,
        failText: "When your plan has started you will not be able to cash it in or dip into it to withdraw lump sums.",
        value: null,
    },
    {
        name: "restOfLife",
        label: "Do you expect your pension fund to provide you with an income for the rest of your life?",
        errorLabel: "rest of your life",
        failState: false,
        failText: `The main aim of an annuity is to provide you with an income for life,
            no matter how long you live. Once in payment, it cannot be changed or cashed in.`,
        value: null,
    },
    {
        name: "medical",
        label: `Are you aware that if you have certain medical conditions and/or are overweight,
            or smoke you may be eligible for a higher level of income from your annuity?`,
        errorLabel: "any medical condition",
        failState: false,
        failText: `If you have certain medical conditions and/or are overweight,
            or smoke you may be able to get a higher level of income through an enhanced annuity.<br><br>
            If you haven't given us your health details, you should return to the
            quote form and complete these now.<br><br>
            If you have less than 12 months to live you may be able to take your full pot tax-free.
            You should discuss this option with your pension provider.`,
        value: null,
    },
    {
        name: "dependants",
        label: `Do you need to use your pension fund to provide for your financial dependants,
            such as a spouse or registered civil partner, when you die?`,
        errorLabel: "financial dependants",
        failState: true,
        failText: `If you choose an annuity which doesn't pay out after your death, the income
            payments will stop when you die and no payments will be made to your dependants,
            even if you die shortly after the plan is set up. If you are not sure whether your annuity
            will pay out after your death, please check your quote.`,
        value: null,
    },
    {
        name: "inflation",
        label: "Have you considered how your income in retirement would keep up with the cost of living (inflation)?",
        errorLabel: "cost of living",
        failState: false,
        failText: `Over time, inflation will increase the cost of living as prices go up. So if your income does not
            increase, then the buying power of your income will reduce over time as goods and services will cost more.`,
        value: null,
    },
    {
        name: "longTermCare",
        label: `Do you know that accessing your pension fund could affect any means tested state benefits
            you or your partner receive now or could claim in the future?<br><br>
            For example housing benefit, council tax reduction, income support, Universal Credit,
            income-based job seeker's allowance and any benefit that pays for long term care.`,
        errorLabel: "tested state benefits",
        failState: false,
        failText: `Your benefits may be reduced or stopped if you have assets or income above a certain level. You
            should check how this might affect you before you take money from your pension fund. For more information
            visit <a href='https://www.gov.uk/browse/benefits' target='_blank'>www.gov.uk/browse/benefits</a>`,
        value: null,
    },
    {
        name: "payments",
        label: `Are you aware of how any money you take out of your pension fund might be treated if you're behind
            on any payments to credit cards, loans or a mortgage?`,
        errorLabel: "credit cards, loans or a mortgage",
        failState: false,
        failText: `Once you have accessed your pension fund any money you take out may be available
            to your creditors in the event that you fail to keep up your repayments to them.`,
        value: null,
    },
    {
        name: "scams",
        label: "Do you know how to protect yourself from investment scams?",
        errorLabel: "investment scams",
        failState: false,
        failText: `You should be careful with the money you're taking from your pension fund as fraudsters may be
            trying to steal your money.<br><br>
            Some schemes can appear very genuine so make sure you are dealing with
            a reputable firm protected by the Financial Services Compensation Scheme.<br><br>
            Also watch out for investments that seem too good to be true.<br><br>
            For more information visit
            <a href='https://www.fca.org.uk/consumers/scams' target='_blank'>www.fca.org.uk/consumers/scams</a>.`,
        value: null,
    },
];

const getPremiumTotalAnnualPension = (premium: AnnuitiesPremium): number => {
    return "totalAnnualPension" in premium.policy ? premium.policy.totalAnnualPension : 0;
};

const mapApplicantDetailsFromQuote = (currentApplicantDetails: AboutYou, quote: QuoteRequest): AboutYou => ({
    ...currentApplicantDetails,
    title: quote.customer.title,
    forename: quote.customer.forename,
    surname: quote.customer.surname,
    dob: quote.customer.dob,
    maritalStatus: quote.customer.maritalStatus,
    phone: quote.customer.phone ?? "",
    email: quote.customer.email,
});

const mapDependantDetailsFromQuote = (dependant: NonNullable<QuoteRequest["dependant"]>): AboutYou => ({
    title: dependant.title ?? null,
    forename: dependant.forename ?? "",
    surname: dependant.surname ?? "",
    dob: dependant.dob,
    maritalStatus: dependant.maritalStatus,
    nationalInsuranceNo: "",
    address: {
        line1: "",
        line2: "",
        city: "",
        county: "",
        postcode: dependant.address?.postcode ?? "",
        country: "",
    },
    phone: "",
    email: "",
    applicantAllowanceProtection: null,
    applicantLifetimeAllowanceTaxProtection: null,
    applicantRemainingIlsa: null,
    seeTheMedicalReport: null,
    doctorsDetails: {
        name: "",
        line1: "",
        line2: "",
        city: "",
        county: "",
        postcode: "",
        country: "",
        phone: "",
    },
});

const mapDependantDetailsFromStore = (): AboutYou | undefined => {
    const dependantAboutYou = useDependantAboutYouStore();
    const hasDependantValue = !!(
        dependantAboutYou.title !== null ||
        dependantAboutYou.forename ||
        dependantAboutYou.surname ||
        dependantAboutYou.dob ||
        dependantAboutYou.postcode
    );

    if (!hasDependantValue) {
        return undefined;
    }

    return {
        title: dependantAboutYou.title,
        forename: dependantAboutYou.forename,
        surname: dependantAboutYou.surname,
        dob: dependantAboutYou.dob,
        maritalStatus: dependantAboutYou.maritalStatus,
        nationalInsuranceNo: "",
        address: {
            line1: "",
            line2: "",
            city: "",
            county: "",
            postcode: dependantAboutYou.postcode,
            country: "",
        },
        phone: "",
        email: "",
        applicantAllowanceProtection: null,
        applicantLifetimeAllowanceTaxProtection: null,
        applicantRemainingIlsa: null,
        seeTheMedicalReport: null,
        doctorsDetails: {
            name: "",
            line1: "",
            line2: "",
            city: "",
            county: "",
            postcode: "",
            country: "",
            phone: "",
        },
    };
};

const getDependantDetails = (quote: QuoteRequest, existingDependantDetails?: AboutYou): AboutYou | undefined => {
    const dependant = quote.dependant;

    if (dependant && Object.keys(dependant).length > 0) {
        const fromQuote = mapDependantDetailsFromQuote(dependant);
        if (existingDependantDetails) {
            // Preserve application-entered fields (NI, full address, doctors, etc.) when re-entering apply.
            // Only update the quote-sourced fields which may have changed.
            return {
                ...existingDependantDetails,
                title: fromQuote.title,
                forename: fromQuote.forename,
                surname: fromQuote.surname,
                dob: fromQuote.dob,
                maritalStatus: fromQuote.maritalStatus,
                address: {
                    ...existingDependantDetails.address,
                    postcode: fromQuote.address.postcode,
                },
            };
        }
        return fromQuote;
    }

    const expectsDependant = quote.product.paySurvivingDependant?.type === PaySurvivingDependantType.Fraction;

    if (expectsDependant && existingDependantDetails) {
        // Preserve dependant values already entered in application flow when quote reload omits dependant payload.
        return {
            ...existingDependantDetails,
        };
    }

    if (expectsDependant) {
        return mapDependantDetailsFromStore();
    }

    return undefined;
};

export const useApplicationStore = defineStore("application", {
    state: (): ApplicationStore => {
        return {
            companyName: "",
            companySlug: "",
            privacyUrl: "",
            illustrationsUrl: "",
            termsConditionsUrl: "",
            keyFeaturesUrl: "",
            totalAnnualPension: 0,
            quoteNumber: "",
            applicationId: null,
            premiumId: 0,
            applicationStage: ApplyPageStages.PensionWise,
            defenceQuestions,
            applicantDetails: [{
                title: null,
                forename: "",
                surname: "",
                dob: "",
                maritalStatus: null,
                nationalInsuranceNo: "",
                address: {
                    line1: "",
                    line2: "",
                    city: "",
                    county: "",
                    postcode: "",
                    country: "",
                },
                phone: "",
                email: "",
                applicantAllowanceProtection: null,
                applicantLifetimeAllowanceTaxProtection: null,
                applicantRemainingIlsa: null,
                doctorsDetails: {
                    name: "",
                    line1: "",
                    line2: "",
                    city: "",
                    county: "",
                    postcode: "",
                    country: "",
                    phone: "",
                },
                seeTheMedicalReport: null,
            }],
            pensionFunds: [],
            paymentInformation: {
                accountNumber: null,
                sortCode: "",
                bankName: "",
                bankBranchName: "",
                address: {
                    line1: "",
                    line2: "",
                    city: "",
                    county: "",
                    postcode: "",
                    country: "",
                },
                accountHolder: "",
                buildingSociety: "",
            },
            pensionFundsModal: false,
            pensionFundsIndex: null,
            applicationRequestStatus: RequestStatus.Idle,
            applicationStatus: null,
            isManualBankEntry: false,
            isPolling: false,
        };
    },
    getters: {
        applicantAboutDetails(state) {
            return ensureDefined(state.applicationStage === ApplyPageStages.AboutYou
                ? state.applicantDetails[0]
                : state.applicantDetails[1]);
        },
    },
    actions: {
        setApplicationDetails(premium: AnnuitiesPremium, quoteNumber: string, quote: QuoteRequest) {
            const existingDependantDetails = this.applicantDetails[1];
            this.companyName = premium.company.name;
            this.companySlug = premium.company.slug;
            this.privacyUrl = premium.company.privacyUrl ?? "";
            this.illustrationsUrl = premium.documents?.illustration ?? "";
            this.termsConditionsUrl = premium.documents?.termsConditions ?? "";
            this.keyFeaturesUrl = premium.documents?.keyFeatures ?? "";
            this.totalAnnualPension = getPremiumTotalAnnualPension(premium);
            this.quoteNumber = quoteNumber;
            this.premiumId = premium.premiumId;
            this.applicationStage = ApplyPageStages.Information;
            this.defenceQuestions = defenceQuestions;

            this.applicantDetails = [
                mapApplicantDetailsFromQuote(this.applicantDetails[0], quote),
            ];

            const dependantDetails = getDependantDetails(quote, existingDependantDetails);
            if (dependantDetails) {
                this.applicantDetails.push(dependantDetails);
            }
        },
    },
});
